import { useState, useEffect, useRef } from "react";
import api from "../lib/api";
import { MapPin } from "lucide-react";

export function AddressAutocomplete({ value, onChange, onSelect, placeholder, country, testid, dotColor = "#EC407A" }) {
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const skipRef = useRef(false);
  const boxRef = useRef(null);
  const reqRef = useRef(0);

  useEffect(() => {
    if (skipRef.current) { skipRef.current = false; return; }
    const q = (value || "").trim();
    if (q.length < 3) { setResults([]); setOpen(false); return; }
    const myId = ++reqRef.current;
    const id = setTimeout(async () => {
      try {
        const { data } = await api.get(`/geo/search`, { params: { q, country: country || "", limit: 5 } });
        if (myId !== reqRef.current) return; // requête périmée : on ignore
        setResults(data.results || []);
        // Ne s'affiche que si le champ est focus ET a des résultats — discret.
        setOpen(focused && (data.results || []).length > 0);
      } catch { if (myId === reqRef.current) setResults([]); }
    }, 350);
    return () => clearTimeout(id);
  }, [value, country, focused]);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) { setOpen(false); setFocused(false); } };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const pick = (r) => {
    skipRef.current = true;
    onChange(r.label);
    onSelect?.(r);
    setOpen(false);
    setResults([]);
  };

  return (
    <div ref={boxRef} className="relative">
      <span className="absolute left-4 top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full z-10" style={{ background: dotColor }} />
      <input
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => { setFocused(true); if (results.length) setOpen(true); }}
        onBlur={() => setTimeout(() => setFocused(false), 150)}
        placeholder={placeholder}
        className="w-full h-14 rounded-2xl border-2 border-[#ECE7DF] focus:border-[#EC407A] outline-none pl-10 pr-4 text-[15px] transition-colors bg-white"
      />
      {open && focused && results.length > 0 && (
        <div data-testid={`${testid}-list`} className="absolute z-[1200] left-0 right-0 mt-1 bg-white/95 backdrop-blur rounded-xl border border-[#ECE7DF]/80 shadow-[0_4px_16px_rgba(0,0,0,0.08)] overflow-hidden max-h-56 overflow-y-auto">
          {results.map((r, i) => (
            <button key={i} type="button" data-testid={`${testid}-opt-${i}`} onClick={() => pick(r)}
              className="w-full text-left px-3 py-2 flex items-start gap-2 hover:bg-[#F0EBE1] transition-colors border-b border-[#F3EFE8]/60 last:border-0">
              <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#75807A]" />
              <span className="text-xs text-[#3B423E] leading-snug line-clamp-1">{r.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
